import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Linkedin, Twitter, Facebook, Instagram, Music2, Send, ExternalLink,
  Sparkles, Trash2, RefreshCw, Copy, Check, AlertCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  useSocialPosts, usePostVariants, useGenerateDraft, useUpdateVariant,
  usePublishVariant, useDeletePost, useSocialSettings, useUpdateSocialSettings,
  SocialPost, SocialPostVariant,
} from "@/hooks/useSocialPosts";
import { NETWORK_LIMITS, NETWORK_LABEL, buildShareIntent, isApiPublish, SocialNetwork } from "@/lib/social";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const NETWORK_ICON: Record<SocialNetwork, React.ComponentType<{ className?: string }>> = {
  linkedin: Linkedin,
  x: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  tiktok: Music2,
};

const STATUS_COLOR: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-primary/15 text-primary border border-primary/30",
  scheduled: "bg-blue-500/15 text-blue-300 border border-blue-500/30",
  published: "bg-green-500/15 text-green-300 border border-green-500/30",
  published_manual: "bg-green-500/15 text-green-300 border border-green-500/30",
  publishing: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  failed: "bg-destructive/15 text-destructive border border-destructive/30",
  skipped: "bg-muted text-muted-foreground",
};

export default function SocialStudio() {
  const [activePostId, setActivePostId] = useState<string | null>(null);
  const { data: posts = [], isLoading } = useSocialPosts();

  useEffect(() => {
    if (!activePostId && posts.length) setActivePostId(posts[0].id);
  }, [posts, activePostId]);

  const activePost = posts.find((p) => p.id === activePostId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold mb-1">Social Studio</h2>
          <p className="text-sm text-muted-foreground">
            Draft, edit, schedule and publish per-network posts. LinkedIn publishes via API; other networks open a pre-filled compose window or export bundle.
          </p>
        </div>
        <div className="flex gap-2">
          <NewDraftButton />
          <SettingsDialog />
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <Card className="p-3 max-h-[70vh] overflow-y-auto">
          {isLoading && (
            <div className="text-sm text-muted-foreground p-4">Loading…</div>
          )}
          {!isLoading && !posts.length && (
            <div className="text-sm text-muted-foreground p-4">
              No drafts yet. Click <span className="font-semibold">New draft</span> to get started.
            </div>
          )}
          <ul className="space-y-1">
            {posts.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => setActivePostId(p.id)}
                  className={`w-full text-left p-3 rounded-md transition-colors ${
                    activePostId === p.id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] uppercase">{p.source_kind}</Badge>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_COLOR[p.status]}`}>
                      {p.status}
                    </span>
                  </div>
                  <div className="text-sm font-medium line-clamp-2">
                    {p.source_title ?? "(untitled)"}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <div>
          {activePost ? (
            <PostEditor post={activePost} />
          ) : (
            <Card className="p-8 text-sm text-muted-foreground text-center">
              Select a draft on the left, or create a new one.
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function PostEditor({ post }: { post: SocialPost }) {
  const { data: variants = [], isLoading } = usePostVariants(post.id);
  const del = useDeletePost();
  const generate = useGenerateDraft();

  return (
    <Card className="p-5 space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Badge variant="outline" className="mb-2 uppercase text-[10px]">
            {post.source_kind}
          </Badge>
          <h3 className="text-lg font-semibold">{post.source_title}</h3>
          {post.source_url && (
            <a
              href={post.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1"
            >
              <ExternalLink className="h-3 w-3" /> {post.source_url}
            </a>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm"
            disabled={generate.isPending}
            onClick={() => {
              if (!post.source_id) {
                toast.error("No source attached — regeneration not supported");
                return;
              }
              generate.mutate({ source_kind: post.source_kind, source_id: post.source_id });
            }}
          >
            <Sparkles className="h-3.5 w-3.5 mr-1" /> Regenerate all
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => {
              if (confirm("Delete this draft and all per-network copies?")) {
                del.mutate(post.id);
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading variants…</div>}

      {!!variants.length && (
        <Tabs defaultValue={variants[0].network}>
          <TabsList className="flex-wrap">
            {variants.map((v) => {
              const Icon = NETWORK_ICON[v.network];
              return (
                <TabsTrigger key={v.id} value={v.network} className="gap-1.5">
                  <Icon className="h-3.5 w-3.5" />
                  {NETWORK_LABEL[v.network]}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {variants.map((v) => (
            <TabsContent key={v.id} value={v.network}>
              <VariantEditor variant={v} post={post} />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function VariantEditor({ variant, post }: { variant: SocialPostVariant; post: SocialPost }) {
  const [body, setBody] = useState(variant.body);
  const [copied, setCopied] = useState(false);
  const update = useUpdateVariant();
  const publish = usePublishVariant();

  useEffect(() => { setBody(variant.body); }, [variant.id, variant.body]);

  const limit = NETWORK_LIMITS[variant.network];
  const over = body.length > limit;

  const save = () => update.mutate({ id: variant.id, patch: { body } });

  const intentHref = useMemo(() => {
    if (variant.network === "linkedin") {
      return post.source_url
        ? buildShareIntent("linkedin", { url: post.source_url, title: post.source_title ?? "" })
        : null;
    }
    if (variant.network === "x") {
      return buildShareIntent("x", {
        url: post.source_url ?? "",
        text: body.slice(0, NETWORK_LIMITS.x - 30),
      });
    }
    if (variant.network === "facebook") {
      return post.source_url
        ? buildShareIntent("facebook", { url: post.source_url })
        : null;
    }
    return null;
  }, [variant.network, post.source_url, post.source_title, body]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Copied caption");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const apiPublish = isApiPublish(variant.network);

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLOR[variant.status]}`}>
          {variant.status}
        </span>
        <span className={`text-[11px] tabular-nums ${over ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
          {body.length} / {limit}
        </span>
      </div>

      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={10}
        className="font-mono text-sm"
        placeholder={`Write your ${NETWORK_LABEL[variant.network]} post…`}
      />

      {variant.error_message && (
        <div className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded p-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{variant.error_message}</span>
        </div>
      )}

      {variant.asset_url && (
        <div className="flex items-center gap-3 p-2 rounded border border-border">
          <img src={variant.asset_url} className="h-14 w-14 rounded object-cover" alt="" />
          <div className="text-xs text-muted-foreground">
            Image attached. {!apiPublish && "Download and post manually for Instagram / TikTok."}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={save} disabled={update.isPending}>
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={copy} className="gap-1.5">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          Copy caption
        </Button>
        {variant.asset_url && (
          <a
            href={variant.asset_url}
            download
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border border-border hover:bg-muted"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Download image
          </a>
        )}

        <div className="ml-auto flex gap-2">
          {apiPublish ? (
            <Button
              size="sm"
              disabled={publish.isPending || over}
              onClick={async () => {
                await update.mutateAsync({ id: variant.id, patch: { body } });
                try {
                  const res = await publish.mutateAsync(variant.id);
                  toast.success(
                    res.externalUrl
                      ? "Posted to LinkedIn"
                      : "Posted (no link returned)",
                  );
                } catch { /* mutation already toasted */ }
              }}
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {publish.isPending ? "Publishing…" : `Publish to ${NETWORK_LABEL[variant.network]}`}
            </Button>
          ) : intentHref ? (
            <Button
              size="sm"
              onClick={async () => {
                await update.mutateAsync({ id: variant.id, patch: { body } });
                window.open(intentHref!, "_blank", "noopener,noreferrer,width=720,height=640");
                if (confirm("Mark this post as published?")) {
                  await update.mutateAsync({
                    id: variant.id,
                    patch: { status: "published_manual", published_at: new Date().toISOString() },
                  });
                }
              }}
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Open in {NETWORK_LABEL[variant.network]}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                await navigator.clipboard.writeText(body);
                toast.success("Caption copied — paste in the app");
                if (confirm("Mark this post as published?")) {
                  await update.mutateAsync({
                    id: variant.id,
                    patch: { status: "published_manual", published_at: new Date().toISOString() },
                  });
                }
              }}
            >
              <Copy className="h-3.5 w-3.5 mr-1.5" />
              Copy + mark posted
            </Button>
          )}
        </div>
      </div>

      {variant.external_post_url && (
        <a
          href={variant.external_post_url}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" /> View live post
        </a>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function NewDraftButton() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"idea" | "article" | "event">("article");
  const [sources, setSources] = useState<Array<{ id: string; label: string }>>([]);
  const [sourceId, setSourceId] = useState<string>("");
  const generate = useGenerateDraft();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (kind === "idea") {
        const { data } = await supabase.from("ideas").select("id,title").eq("status", "published").order("created_at", { ascending: false }).limit(50);
        if (!cancelled) setSources((data ?? []).map((d) => ({ id: d.id, label: d.title })));
      } else if (kind === "article") {
        const { data } = await supabase.from("articles").select("id,title").eq("published", true).order("published_at", { ascending: false }).limit(50);
        if (!cancelled) setSources((data ?? []).map((d) => ({ id: d.id, label: d.title })));
      } else {
        setSources([]);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Sparkles className="h-3.5 w-3.5 mr-1.5" /> New draft
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate social drafts</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Source type</Label>
            <Select value={kind} onValueChange={(v) => { setKind(v as never); setSourceId(""); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="article">Article (from Insights)</SelectItem>
                <SelectItem value="idea">Idea (from Discover)</SelectItem>
                <SelectItem value="event">Event (generic)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {kind !== "event" ? (
            <div>
              <Label className="text-xs">Pick a {kind}</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger><SelectValue placeholder={`Choose ${kind}…`} /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <Input
              placeholder="Event identifier or short slug"
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
            />
          )}
          <Button
            className="w-full"
            disabled={!sourceId || generate.isPending}
            onClick={async () => {
              try {
                await generate.mutateAsync({ source_kind: kind, source_id: sourceId });
                setOpen(false);
              } catch {/* toasted */}
            }}
          >
            {generate.isPending ? "Generating…" : "Generate 5-platform draft"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */

function SettingsDialog() {
  const { data: settings } = useSocialSettings();
  const update = useUpdateSocialSettings();
  const [open, setOpen] = useState(false);
  const [hashtags, setHashtags] = useState("");
  const [signature, setSignature] = useState("");
  const [autoArticles, setAutoArticles] = useState(true);
  const [autoIdeas, setAutoIdeas] = useState(false);
  const [autoEvents, setAutoEvents] = useState(true);

  useEffect(() => {
    if (settings) {
      setHashtags(settings.default_hashtags.join(", "));
      setSignature(settings.signature ?? "");
      setAutoArticles(settings.auto_draft_articles);
      setAutoIdeas(settings.auto_draft_ideas);
      setAutoEvents(settings.auto_draft_events);
    }
  }, [settings]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">Settings</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Social Studio settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">Default hashtags (comma separated)</Label>
            <Input value={hashtags} onChange={(e) => setHashtags(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Brand signature</Label>
            <Input value={signature} onChange={(e) => setSignature(e.target.value)} />
          </div>
          <div className="space-y-3 pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Phase 3 (not yet active): auto-create a draft when new content is published. Settings persist now; trigger hookup comes next.
            </p>
            <ToggleRow label="Auto-draft new articles" checked={autoArticles} onChange={setAutoArticles} />
            <ToggleRow label="Auto-draft new ideas" checked={autoIdeas} onChange={setAutoIdeas} />
            <ToggleRow label="Auto-draft new events" checked={autoEvents} onChange={setAutoEvents} />
          </div>
          <Button
            className="w-full"
            disabled={update.isPending}
            onClick={async () => {
              await update.mutateAsync({
                default_hashtags: hashtags.split(",").map((h) => h.trim()).filter(Boolean),
                signature,
                auto_draft_articles: autoArticles,
                auto_draft_ideas: autoIdeas,
                auto_draft_events: autoEvents,
              });
              setOpen(false);
            }}
          >
            Save settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}